/**
 * RiskCaseReviewPanel — TN-IPDS AI Risk Case Review
 *
 * Lists ai_cases with status=open_pending_review.
 * Shows AI recommendation, citations, risk indicators (non-accusatory).
 * Approve / Reject / Dismiss actions route through the Human Approval Gate.
 *
 * API:
 *   GET  /v1/tn-ipds/ai-cases
 *   POST /v1/tn-ipds/ai-cases/:id/approve
 *   POST /v1/tn-ipds/ai-cases/:id/reject
 *   POST /v1/tn-ipds/ai-cases/:id/dismiss
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../config/apiConfig';

const SEVERITY_COLORS = {
  critical: { bg: '#fee2e2', color: '#991b1b', label: '🔴 Critical' },
  high:     { bg: '#fef3c7', color: '#92400e', label: '🟠 High' },
  medium:   { bg: '#eff6ff', color: '#1e40af', label: '🔵 Medium' },
  low:      { bg: '#f0fdf4', color: '#065f46', label: '🟢 Low' },
};

const s = {
  container: { fontFamily: 'sans-serif', maxWidth: 960, margin: '0 auto', padding: 24 },
  title: { fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 },
  subtitle: { fontSize: 13, color: '#666', marginBottom: 20 },
  card: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
    padding: 20, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  badge: (sev) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 20,
    fontSize: 12, fontWeight: 700,
    background: SEVERITY_COLORS[sev]?.bg || '#f3f4f6',
    color: SEVERITY_COLORS[sev]?.color || '#374151',
  }),
  btn: { padding: '7px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginRight: 8 },
  btnApprove: { background: '#d1fae5', color: '#065f46' },
  btnReject:  { background: '#fee2e2', color: '#991b1b' },
  btnDismiss: { background: '#f3f4f6', color: '#6b7280' },
  citationChip: {
    display: 'inline-block', background: '#eff6ff', color: '#1e40af',
    padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    marginRight: 4, marginBottom: 4,
  },
  riskScore: (score) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 20,
    fontSize: 12, fontWeight: 700,
    background: score >= 0.8 ? '#fee2e2' : score >= 0.5 ? '#fef3c7' : '#f0fdf4',
    color: score >= 0.8 ? '#991b1b' : score >= 0.5 ? '#92400e' : '#065f46',
  }),
  empty: { textAlign: 'center', padding: 48, color: '#aaa', fontSize: 14 },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 12 },
  filterRow: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
  filterBtn: (active) => ({
    padding: '5px 14px', borderRadius: 20, border: '1px solid #d1d5db',
    cursor: 'pointer', fontSize: 12, fontWeight: 600,
    background: active ? '#4f46e5' : '#fff',
    color: active ? '#fff' : '#374151',
  }),
};

const RiskCaseReviewPanel = ({ clientId, apiBase }) => {
  const base = apiBase || API_BASE || '/v1';

  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [actionMessages, setActionMessages] = useState({});
  const [severityFilter, setSeverityFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ status: 'open_pending_review' });
      if (clientId) params.set('client_id', clientId);
      const res = await fetch(`${base}/tn-ipds/ai-cases?${params}`);
      if (!res.ok) throw new Error(`Failed to load cases (${res.status})`);
      const data = await res.json();
      setCases(data.cases || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [base, clientId]);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  const handleAction = async (caseId, action) => {
    setActionLoading(prev => ({ ...prev, [caseId]: action }));
    setActionMessages(prev => ({ ...prev, [caseId]: '' }));
    try {
      const res = await fetch(`${base}/tn-ipds/ai-cases/${caseId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Action failed (${res.status})`);
      }
      setActionMessages(prev => ({
        ...prev,
        [caseId]: `✅ Case ${action}d successfully. Routed to Workflow Agent.`,
      }));
      // Remove from list after short delay
      setTimeout(() => {
        setCases(prev => prev.filter(c => c.case_id !== caseId));
      }, 2000);
    } catch (err) {
      setActionMessages(prev => ({ ...prev, [caseId]: `❌ ${err.message}` }));
    } finally {
      setActionLoading(prev => ({ ...prev, [caseId]: null }));
    }
  };

  const filtered = severityFilter === 'all'
    ? cases
    : cases.filter(c => c.severity_level === severityFilter);

  const severityCounts = cases.reduce((acc, c) => {
    acc[c.severity_level] = (acc[c.severity_level] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={s.container}>
      <h2 style={s.title}>🚨 AI Risk Case Review</h2>
      <p style={s.subtitle}>
        Review AI-detected procurement anomalies. All findings use non-accusatory language
        and require human approval before any official action is taken.
      </p>

      {error && <div style={s.error} role="alert">⚠ {error}</div>}

      {/* Severity filter */}
      <div style={s.filterRow}>
        <span style={{ fontSize: 12, color: '#888', fontWeight: 600 }}>Filter:</span>
        {['all', 'critical', 'high', 'medium', 'low'].map(sev => (
          <button
            key={sev}
            style={s.filterBtn(severityFilter === sev)}
            onClick={() => setSeverityFilter(sev)}
          >
            {sev === 'all' ? `All (${cases.length})` : `${sev} (${severityCounts[sev] || 0})`}
          </button>
        ))}
        <button
          style={{ ...s.btn, marginLeft: 'auto', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}
          onClick={fetchCases}
          disabled={loading}
        >
          {loading ? '⏳' : '↺ Refresh'}
        </button>
      </div>

      {loading && <div style={s.empty}>Loading risk cases…</div>}

      {!loading && filtered.length === 0 && (
        <div style={s.empty}>
          {cases.length === 0
            ? '✅ No open risk cases pending review.'
            : `No ${severityFilter} severity cases.`}
        </div>
      )}

      {filtered.map(c => {
        const isExpanded = expandedId === c.case_id;
        const sev = c.severity_level || 'medium';
        const score = parseFloat(c.ai_risk_score || 0);
        const actionMsg = actionMessages[c.case_id];
        const isActing = actionLoading[c.case_id];

        return (
          <div key={c.case_id} style={s.card}>
            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <span style={s.badge(sev)}>{SEVERITY_COLORS[sev]?.label || sev}</span>
                {' '}
                <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>
                  {c.case_type?.replace(/_/g, ' ') || 'Anomaly Detected'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={s.riskScore(score)}>Risk: {Math.round(score * 100)}%</span>
                <button
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}
                  onClick={() => setExpandedId(isExpanded ? null : c.case_id)}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? '▲' : '▼'}
                </button>
              </div>
            </div>

            {/* Tender reference */}
            {c.tender_reference && (
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                Tender: <strong>{c.tender_reference}</strong>
                {c.department_name && ` · ${c.department_name}`}
                {c.district_name && ` · ${c.district_name}`}
              </div>
            )}

            {/* AI Recommendation (non-accusatory) */}
            {c.ai_recommendation && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: '#f9fafb', borderRadius: 6, fontSize: 13, color: '#374151', borderLeft: '3px solid #4f46e5' }}>
                <strong>AI Observation:</strong> {c.ai_recommendation}
              </div>
            )}

            {/* Citations */}
            {isExpanded && c.policy_citations && c.policy_citations.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 4 }}>POLICY CITATIONS</div>
                {c.policy_citations.map((cit, i) => (
                  <span key={i} style={s.citationChip}>[{cit}]</span>
                ))}
              </div>
            )}

            {/* Evidence details */}
            {isExpanded && c.evidence && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 4 }}>EVIDENCE</div>
                <pre style={{ fontSize: 12, background: '#f3f4f6', padding: 10, borderRadius: 6, overflow: 'auto', maxHeight: 200 }}>
                  {typeof c.evidence === 'string' ? c.evidence : JSON.stringify(c.evidence, null, 2)}
                </pre>
              </div>
            )}

            {/* Action message */}
            {actionMsg && (
              <div style={{
                marginTop: 10, padding: '8px 12px', borderRadius: 6, fontSize: 13,
                background: actionMsg.startsWith('✅') ? '#f0fdf4' : '#fef2f2',
                color: actionMsg.startsWith('✅') ? '#16a34a' : '#dc2626',
              }}>
                {actionMsg}
              </div>
            )}

            {/* Action buttons */}
            {!actionMsg?.startsWith('✅') && (
              <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  style={{ ...s.btn, ...s.btnApprove, opacity: isActing ? 0.6 : 1 }}
                  onClick={() => handleAction(c.case_id, 'approve')}
                  disabled={!!isActing}
                  title="Confirm as a compliance concern — routes to Workflow Agent for officer review"
                >
                  {isActing === 'approve' ? '⏳' : '✅'} Confirm Issue
                </button>
                <button
                  style={{ ...s.btn, ...s.btnReject, opacity: isActing ? 0.6 : 1 }}
                  onClick={() => handleAction(c.case_id, 'reject')}
                  disabled={!!isActing}
                  title="Mark as false positive — no further action"
                >
                  {isActing === 'reject' ? '⏳' : '❌'} False Positive
                </button>
                <button
                  style={{ ...s.btn, ...s.btnDismiss, opacity: isActing ? 0.6 : 1 }}
                  onClick={() => handleAction(c.case_id, 'dismiss')}
                  disabled={!!isActing}
                  title="Dismiss — acknowledged but no action required"
                >
                  {isActing === 'dismiss' ? '⏳' : '⏭'} Dismiss
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RiskCaseReviewPanel;
